import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api-client';
import { Wrench, Save, Plus, Trash2, Check, X, RefreshCw } from 'lucide-react';

export default function AdminConfig() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newConfig, setNewConfig] = useState({ key: '', value: '', config_type: 'string', category: 'general', description: '' });

  const isSuperuser = user?.is_superuser || user?.is_admin || user?.is_staff;

  useEffect(() => {
    if (!isSuperuser) {
      navigate('/dashboard');
      return;
    }
    loadConfigs();
  }, [isSuperuser, navigate]);

  const loadConfigs = () => {
    setLoading(true);
    api.getConfigs()
      .then(setConfigs)
      .catch(err => console.error('Failed to load configs:', err))
      .finally(() => setLoading(false));
  };

  const handleEdit = (config) => {
    setEditingKey(config.key);
    setEditValue(config.is_sensitive ? '' : config.value);
  };

  const handleSave = async (key) => {
    setSaving(key);
    try {
      const updated = await api.updateConfig(key, editValue);
      setConfigs(prev => prev.map(c => c.key === key ? updated : c));
      setEditingKey(null);
    } catch (err) {
      console.error('Failed to save config:', err);
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (key) => {
    if (!window.confirm(`Delete config "${key}"?`)) return;
    try {
      await api.deleteConfig(key);
      setConfigs(prev => prev.filter(c => c.key !== key));
    } catch (err) {
      console.error('Failed to delete config:', err);
    }
  };

  const handleAddConfig = async () => {
    if (!newConfig.key) return;
    try {
      const created = await api.createConfig(newConfig);
      setConfigs(prev => [...prev, created]);
      setShowAddModal(false);
      setNewConfig({ key: '', value: '', config_type: 'string', category: 'general', description: '' });
    } catch (err) {
      console.error('Failed to create config:', err);
    }
  };

  if (!isSuperuser) return null;

  const categories = [...new Set(configs.map(c => c.category))];

  return (
    <div data-testid="admin-config" className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Wrench className="text-amber-500" size={24} />
          <div>
            <h1 className="text-2xl font-bold text-white font-mono">Platform Config</h1>
            <p className="text-sm text-zinc-500">Manage platform-wide settings</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadConfigs}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-800 text-zinc-300 rounded-sm hover:bg-zinc-700 transition-all font-mono text-sm"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-black rounded-sm hover:bg-emerald-400 transition-all font-mono text-sm font-medium"
          >
            <Plus size={14} />
            Add Config
          </button>
        </div>
      </div>

      {/* Configs by Category */}
      {loading ? (
        <div className="space-y-4">
          {[1,2,3].map(i => (
            <div key={i} className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-4 animate-pulse">
              <div className="h-4 bg-zinc-800 rounded w-1/4 mb-4"></div>
              <div className="space-y-2">
                {[1,2].map(j => <div key={j} className="h-10 bg-zinc-800 rounded"></div>)}
              </div>
            </div>
          ))}
        </div>
      ) : categories.map(category => (
        <div key={category} className="bg-[#0A0A0A] border border-zinc-800 rounded-sm">
          <div className="p-4 border-b border-zinc-800">
            <h2 className="text-white font-mono font-medium capitalize">{category}</h2>
          </div>
          <div className="divide-y divide-zinc-800">
            {configs.filter(c => c.category === category).map(config => (
              <div key={config.key} className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <code className="text-emerald-400 font-mono text-sm">{config.key}</code>
                      <span className="text-xs px-1.5 py-0.5 bg-zinc-800 text-zinc-400 rounded font-mono">{config.config_type}</span>
                      {config.is_sensitive && <span className="text-xs px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded font-mono">sensitive</span>}
                    </div>
                    <p className="text-xs text-zinc-500 mb-2">{config.description}</p>
                    
                    {editingKey === config.key ? (
                      <div className="flex items-center gap-2">
                        {config.config_type === 'boolean' ? (
                          <select
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="flex-1 bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-emerald-500 outline-none"
                          >
                            <option value="true">true</option>
                            <option value="false">false</option>
                          </select>
                        ) : (
                          <input
                            type={config.config_type === 'number' ? 'number' : 'text'}
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="flex-1 bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-emerald-500 outline-none"
                            placeholder={config.is_sensitive ? 'Enter new value...' : ''}
                          />
                        )}
                        <button
                          onClick={() => handleSave(config.key)}
                          disabled={saving === config.key}
                          className="p-2 bg-emerald-500 text-black rounded-sm hover:bg-emerald-400 transition-all"
                        >
                          {saving === config.key ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
                        </button>
                        <button
                          onClick={() => setEditingKey(null)}
                          className="p-2 bg-zinc-700 text-zinc-300 rounded-sm hover:bg-zinc-600 transition-all"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <code className="text-zinc-300 font-mono text-sm bg-zinc-900 px-2 py-1 rounded">{config.value}</code>
                      </div>
                    )}
                  </div>
                  
                  {editingKey !== config.key && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleEdit(config)}
                        className="p-2 text-zinc-500 hover:text-emerald-400 transition-all"
                        data-testid={`edit-config-${config.key}`}
                      >
                        <Save size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(config.key)}
                        className="p-2 text-zinc-500 hover:text-red-400 transition-all"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Add Config Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-white font-mono mb-4">Add New Config</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-mono text-zinc-500 mb-1">Key *</label>
                <input
                  type="text"
                  value={newConfig.key}
                  onChange={(e) => setNewConfig({...newConfig, key: e.target.value})}
                  className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-emerald-500 outline-none"
                  placeholder="config_key_name"
                />
              </div>
              <div>
                <label className="block text-xs font-mono text-zinc-500 mb-1">Value</label>
                <input
                  type="text"
                  value={newConfig.value}
                  onChange={(e) => setNewConfig({...newConfig, value: e.target.value})}
                  className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-emerald-500 outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-mono text-zinc-500 mb-1">Type</label>
                  <select
                    value={newConfig.config_type}
                    onChange={(e) => setNewConfig({...newConfig, config_type: e.target.value})}
                    className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-emerald-500 outline-none"
                  >
                    <option value="string">String</option>
                    <option value="number">Number</option>
                    <option value="boolean">Boolean</option>
                    <option value="json">JSON</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-mono text-zinc-500 mb-1">Category</label>
                  <input
                    type="text"
                    value={newConfig.category}
                    onChange={(e) => setNewConfig({...newConfig, category: e.target.value})}
                    className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-emerald-500 outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-mono text-zinc-500 mb-1">Description</label>
                <input
                  type="text"
                  value={newConfig.description}
                  onChange={(e) => setNewConfig({...newConfig, description: e.target.value})}
                  className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-emerald-500 outline-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 bg-zinc-800 text-zinc-300 rounded-sm hover:bg-zinc-700 transition-all font-mono text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleAddConfig}
                disabled={!newConfig.key}
                className="px-4 py-2 bg-emerald-500 text-black rounded-sm hover:bg-emerald-400 transition-all font-mono text-sm font-medium disabled:opacity-50"
              >
                Create Config
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
